/**
 * GasTariffSummary - Display selected gas tariff details
 *
 * Shows tariff name, utility, rate schedule, customer charge,
 * tier table, and approval status in a readable card format.
 */

import React, { useState, useEffect } from 'react';
import {
    Card,
    CardContent,
    Typography,
    Table,
    TableBody,
    TableRow,
    TableCell,
    Box,
    Chip,
    CircularProgress,
    Alert
} from '@mui/material';
import axios from 'axios';
import { useAuth } from '@vortx/shared-ui';

/**
 * GasTariffSummary Component
 *
 * @param {string} tariffId - ID of the gas tariff to display
 */
export default function GasTariffSummary({ tariffId }) {
    const { currentUser } = useAuth();
    const [tariff, setTariff] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (tariffId) {
            loadTariff();
        }
    }, [tariffId, currentUser]);

    const loadTariff = async () => {
        setLoading(true);
        setError(null);

        try {
            // Get auth headers
            const headers = { 'Content-Type': 'application/json' };
            if (currentUser) {
                const token = await currentUser.getIdToken();
                headers['Authorization'] = `Bearer ${token}`;
            } else {
                headers['X-Dev-User-Id'] = 'tim@vortx.us';
            }

            // Fetch tariff details
            const response = await axios.get(
                `/api/site_dna/v1/projects/custom-rates/${tariffId}`,
                { headers }
            );

            setTariff(response.data);
        } catch (err) {
            console.error('Failed to load tariff:', err);
            setError('Failed to load gas tariff details');
        } finally {
            setLoading(false);
        }
    };

    if (loading) {
        return (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 2 }}>
                <CircularProgress size={20} />
                <Typography variant="body2" color="text.secondary">
                    Loading tariff details...
                </Typography>
            </Box>
        );
    }

    if (error) {
        return (
            <Alert severity="error" sx={{ mt: 2 }}>
                {error}
            </Alert>
        );
    }

    if (!tariff) {
        return null;
    }

    const { rate_data } = tariff;

    // Ensure rate_data exists and has required fields
    if (!rate_data || !rate_data.tiers || rate_data.tiers.length === 0) {
        return (
            <Alert severity="warning" sx={{ mt: 2 }}>
                Tariff data is incomplete or invalid
            </Alert>
        );
    }

    return (
        <Card variant="outlined" sx={{ mt: 2 }}>
            <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="h6">
                        {tariff.tariff_name || tariff.name || 'Unnamed Tariff'}
                    </Typography>
                    <Chip
                        label={tariff.status || 'draft'}
                        color={tariff.status === 'approved' ? 'success' : 'warning'}
                        size="small"
                    />
                </Box>

                {tariff.utility_name && (
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                        {tariff.utility_name}
                        {tariff.rate_schedule && ` - ${tariff.rate_schedule}`}
                    </Typography>
                )}

                {rate_data.customer_charge_per_day > 0 && (
                    <Typography variant="body2" sx={{ mt: 1 }}>
                        Customer Charge: ${rate_data.customer_charge_per_day.toFixed(5)}/day
                    </Typography>
                )}

                <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>
                    Usage Tiers:
                </Typography>

                <Table size="small">
                    <TableBody>
                        {rate_data.tiers.map((tier) => {
                            // Check if this tier has the delivery/commodity breakdown
                            const hasBreakdown = tier.delivery_rate_per_therm !== undefined
                                && tier.commodity_rate_per_therm !== undefined;

                            return (
                                <TableRow key={tier.tier_number}>
                                    <TableCell>Tier {tier.tier_number}</TableCell>
                                    <TableCell>
                                        {tier.max_usage_therms
                                            ? `Up to ${tier.max_usage_therms.toLocaleString()} therms`
                                            : 'Unlimited'}
                                    </TableCell>
                                    <TableCell align="right">
                                        {hasBreakdown ? (
                                            <Box component="span">
                                                <Typography variant="body2" component="span" color="text.secondary">
                                                    Delivery: ${tier.delivery_rate_per_therm.toFixed(5)}
                                                </Typography>
                                                {' | '}
                                                <Typography variant="body2" component="span" color="text.secondary">
                                                    Commodity: ${tier.commodity_rate_per_therm.toFixed(5)}
                                                </Typography>
                                                {' | '}
                                                <Typography variant="body2" component="span" fontWeight="medium">
                                                    Total: ${tier.rate_per_therm.toFixed(5)}/therm
                                                </Typography>
                                            </Box>
                                        ) : (
                                            // Legacy format: only rate_per_therm exists
                                            `$${tier.rate_per_therm.toFixed(5)}/therm`
                                        )}
                                    </TableCell>
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
            </CardContent>
        </Card>
    );
}
